import type { Definition } from 'typescript-json-schema';
import type { ControlTypeFunction } from '@sap/ux-specification-types';
import { FACETTITLEPREFIX, BINDINGPROPERTYREGEXSTRING, VOCWITHSLASH, PropertyName } from '@sap/ux-specification-types';
import { DEFINITION_LINK_PREFIX } from '../utils';

/**
 * General export rule, joining all given breadcrumbs to the base ID.
 *
 * @param baseId - base ID, first part of the stable ID
 * @param idBreadcrumbs - array of ID parts
 * @returns the constructed stable ID as a string
 */
export const buildControlIdFromParent = (baseId: string, idBreadcrumbs: string[]): string => {
    return baseId + idBreadcrumbs.join('::');
};

/**
 * Constructs a stable ID for pages by combining the base ID, ID breadcrumbs, and the first breadcrumb.
 *
 * @param baseId - base ID, first part of the stable ID
 * @param idBreadcrumbs - array of ID parts
 * @param breadcrumbs - array of breadcrumbs
 * @returns the constructed stable ID as a string
 */
export const buildControlIdForPage = (baseId: string, idBreadcrumbs: string[], breadcrumbs: string[]): string => {
    return baseId + idBreadcrumbs.join('::') + breadcrumbs[0];
};

/**
 * Converts the section id to the right string for the stable id:
 * If the corresponding facet has ID maintained, we must take this ID. If ID is not present, take the navigation path.
 *
 * @param sectionId - old ID that shall get converted
 * @param title - title from JSON schema, comprising the facet ID
 * @returns the converted section ID as a string
 */
export const convertSectionId = (sectionId: string, title: string): string => {
    let convertedSectionId: string;
    const prefix: string = FACETTITLEPREFIX;
    if (title !== undefined && title.includes(prefix)) {
        convertedSectionId = title.split(prefix)[1];
    } else {
        convertedSectionId = sectionId.replace(new RegExp('/@', 'g'), '::').replace(/@com.sap/g, 'com.sap');
    }
    return convertedSectionId;
};

/**
 * Escapes all characters not allowed in stable IDs with a colon followed by their hexadecimal code.
 *
 * @param sParam - The string to be escaped.
 * @returns The escaped string.
 */
export function escapeId(sParam: string): string {
    /* escape all characters not allowed in stable ids with :<hexcode>
     * as we use : as escape character, also escape :
     */
    return sParam.replace(/[^-A-Za-z0-9_.:]/g, function (c) {
        const sCode = c.charCodeAt(0).toString(16);
        return `:${sCode.length === 1 ? '0' : ''}${sCode}`;
    });
}

/**
 * V2 - Binding Change values, will be added on object pages
 */
export enum BindingValue {
    Editable = '{ui>/editable}',
    NotEditable = '{= !${ui>/editable}}'
}

/**
 * V4 - Binding Change values, will be added on object pages
 */
export enum BindingValueV4 {
    Editable = '{ui>/isEditable}',
    NotEditable = '{= !${ui>/isEditable}}'
}

/**
 * Consolidates the list of id's and other breadcrumbs into one list, for finding the right stable ID of a column.
 *
 * @param idBreadcrumbs - array of ID parts
 * @param localBreadcrumbs - array of breadcrumbs
 * @param title - (section) title = facet ID
 * @returns the consolidated breadcrumbs' list
 */
function mapBreadcrumbs(idBreadcrumbs: string[], localBreadcrumbs: string[], title: string) {
    let sectionId: string;
    idBreadcrumbs = idBreadcrumbs.map(function (item) {
        if (item === PropertyName.sections) {
            for (let index = 0; index < localBreadcrumbs.length; index++) {
                const breadcrumb = localBreadcrumbs[index];
                if (breadcrumb === PropertyName.sections) {
                    sectionId = localBreadcrumbs[index + 1];
                    localBreadcrumbs[index + 1] = convertSectionId(sectionId, title) + '::Table';
                    return localBreadcrumbs[index + 1];
                }
            }
        }
        return item;
    });
    return { idBreadcrumbs };
}

/**
 * Export rule for table columns.
 *
 * @param baseId - base ID, first part of the stable ID. Represents the stableId of the table.
 * @param idBreadcrumbs - array of ID parts, these are ID's that had been calculated on higher levels of the hierarchy
 * @param breadcrumbs - array of breadcrumbs, i.e. the path elements to the column in the config file
 * @param _controlType - controlType function of decorators, not used here
 * @param title - title from JSON schema, comprising the facet ID
 * @returns the constructed stable ID as a string
 */
export const buildColumnControlId = (
    baseId: string,
    idBreadcrumbs: string[],
    breadcrumbs: string[],
    _controlType: ControlTypeFunction,
    title: string
): string => {
    let tableId = 'listReport'; //default
    const localBreadcrumbs = [...breadcrumbs];

    ({ idBreadcrumbs } = mapBreadcrumbs(idBreadcrumbs, localBreadcrumbs, title));
    if (localBreadcrumbs[0] === PropertyName.sections) {
        //special handling for section tables
        tableId = localBreadcrumbs[1]
            .replace(/::com.sap.vocabularies/g, VOCWITHSLASH)
            .replace(/::@com.sap.vocabularies/g, VOCWITHSLASH);
    } else if (idBreadcrumbs[0] === PropertyName.table) {
        tableId = idBreadcrumbs[0];
    }
    const columnId = localBreadcrumbs[localBreadcrumbs.length - 1];
    let encodedColumnId = columnId
        .replace(/::com.sap.vocabularies/g, VOCWITHSLASH)
        .replace(/::@com.sap.vocabularies/g, VOCWITHSLASH)
        .replace('sTarget/@', 'sTarget::@');
    let fullId: string;
    const splitPoint = encodedColumnId.indexOf(':::');
    if (encodedColumnId.startsWith('DataField') && splitPoint > -1) {
        //DataFieldForAction, DataFieldWithIntentBasedNavigation, DataFieldWithNavigationPath, ...
        encodedColumnId = encodedColumnId.replace('/', ':2f');
        fullId = `${baseId}template:::TableColumn:::${encodedColumnId.substring(
            0,
            splitPoint
        )}:::sSmartTableId::${tableId.replace('::Table', ':3a:3aTable')}:::${encodedColumnId.substring(splitPoint + 3)}`;
    } else {
        //Columns of type DataField, they do not have any DataField::: prefix
        //==> legacy encoding, for smart table columns only
        encodedColumnId = encodedColumnId.replace('/', '_');
        fullId = `${baseId}${idBreadcrumbs.join('::')}-${encodedColumnId}`;
    }
    return escapeId(fullId);
};

/**
 * Split up action breadcrumb and return different parts from it.
 *
 * @param breadcrumbs - array of breadcrumbs
 * @returns {object} - actionType, semanticObjectId, actionId of action
 */
export const getActionBreadcrumbParts = (
    breadcrumbs: string[]
): { actionType: string | undefined; semanticObjectId: string | undefined; actionId: string } => {
    const actionIdParts = breadcrumbs[breadcrumbs.length - 1].split(':::');
    let actionType, semanticObjectId, actionId;
    if (actionIdParts.length > 2) {
        actionType = actionIdParts[0];
        semanticObjectId = actionIdParts[1].split('::')[1];
        actionId = actionIdParts[2].split('::')[1];
    } else if (actionIdParts.length === 2) {
        actionType = actionIdParts[0];
        actionId = actionIdParts[1].split('sAction::')[1];
    } else {
        actionId = actionIdParts[0];
    }
    return { actionType, semanticObjectId, actionId };
};

/**
 * Determines the valid pattern for a binding change.
 *
 * @param schemaDefinition - definition in the JSON schema
 * @returns the pattern as string
 */
function determinePattern(schemaDefinition: Definition): string {
    let pattern = BINDINGPROPERTYREGEXSTRING;
    if (schemaDefinition.enum) {
        let enumPattern;
        schemaDefinition.enum.forEach((enumValue) => {
            if (!enumValue.toString().startsWith('{')) {
                enumPattern = enumPattern ? `${enumPattern}|${enumValue.toString()}` : enumValue.toString();
            }
        });
        pattern = `^(${enumPattern})$|${pattern}`;
    }
    if (schemaDefinition['type'] && schemaDefinition['type'] === 'boolean') {
        pattern = `^(false|true)$|${pattern}`;
    }
    return pattern;
}

/**
 * Reuseable function for strings that allow binding changes.
 *
 * @param schema - the complete JSON schema
 * @param definition - definition in the JSON schema, to be updated
 * @param propertyName - property in the properties' list of the given definition
 * @param bindingValues - values that shall be added to the resulting enum
 */
const addPatternForBindingChangeOfEnum = (
    schema: object,
    definition: object,
    propertyName: string,
    bindingValues?: BindingValue[] | BindingValueV4[]
): void => {
    const propertyList = definition && definition['properties'];
    if (propertyList[propertyName].pattern) {
        return; //as already processed
    }
    const originalDefinition = propertyList[propertyName];
    let resultDefinition = originalDefinition;
    const targetDefinition = originalDefinition.$ref
        ? schema['definitions'][originalDefinition.$ref.split(DEFINITION_LINK_PREFIX)[1]]
        : originalDefinition;
    const description = originalDefinition.description ? originalDefinition.description : targetDefinition.description;
    const pattern = determinePattern(targetDefinition);
    if (targetDefinition['type'] && targetDefinition['type'] === 'boolean') {
        //Always an enum, always with more than one value
        resultDefinition = { enum: [false, true] };
    }
    if (bindingValues && resultDefinition['enum']) {
        bindingValues.forEach((value) => {
            if (resultDefinition['enum'].indexOf(value) === -1) {
                resultDefinition['enum'].push(value);
            }
        });
    }
    propertyList[propertyName] = {
        pattern: pattern,
        anyOf: [
            resultDefinition,
            {
                type: 'string'
            }
        ]
    };
    if (description) {
        propertyList[propertyName].description = description;
        delete originalDefinition.description;
    }
};

export const addPatternForBindingChangeOfEnumLR = (schema: object, definition: object, propertyName: string): void => {
    addPatternForBindingChangeOfEnum(schema, definition, propertyName);
};

export const addPatternForBindingChangeOfEnumOP = (schema: object, definition: object, propertyName: string): void => {
    const bindingValues = Object.values(BindingValue);
    addPatternForBindingChangeOfEnum(schema, definition, propertyName, bindingValues);
};

export const addPatternForBindingChangeOfEnumOPV4 = (
    schema: object,
    definition: object,
    propertyName: string
): void => {
    const bindingValues = Object.values(BindingValueV4);
    addPatternForBindingChangeOfEnum(schema, definition, propertyName, bindingValues);
};

/**
 * Sync rule for preventing the deletion of a property.
 */
export function doNotDelete(): void {
    return;
}
